// src/validation/mediationHealth.js
// SDK Bidding / mediation buyer health rollup.
//
// GAM "SDK Bidding" lets trusted third-party buyers (Liftoff, AppLovin, Meta,
// Mintegral, Pangle, Unity, InMobi, DT Exchange, BidMachine, Moloco, LY Ads)
// bid AND render via their OWN SDK. Setup requires, per buyer: install the SDK,
// allow SDK Bidding, enable secure signals, and add ad-unit mappings.
//
// Ad Inspector's "single ad source" test verifies one buyer at a time on a
// registered test device. A proxy can't force that, so this reconciles what
// Publisher Settings DECLARES (mediation adapters that are SDK-Bidding buyers)
// against what is actually OBSERVED firing (sdk_bidding rows captured live) —
// the always-on, production-traffic equivalent: per buyer, did its SDK fire,
// bid, fill, render, or error?
//
// Mirrors buildSecureSignalsHealth so it renders as a peer health rollup.

// Known SDK-Bidding buyers and the keywords that identify them in a declared
// adapter's friendly name (from Publisher Settings) or an observed buyer name.
const BUYERS = [
    { id: "liftoff",     name: "Liftoff Monetize (Vungle)", re: /liftoff|vungle/i },
    { id: "applovin",    name: "AppLovin",                  re: /applovin/i },
    { id: "meta",        name: "Meta Audience Network",     re: /\bmeta\b|facebook|audience network/i },
    { id: "mintegral",   name: "Mintegral",                 re: /mintegral|rayjump/i },
    { id: "pangle",      name: "Pangle",                    re: /pangle|pangolin/i },
    { id: "unity",       name: "Unity Ads",                 re: /unity/i },
    { id: "inmobi",      name: "InMobi",                    re: /inmobi/i },
    { id: "dt_exchange", name: "DT Exchange (Fyber)",       re: /dt ?exchange|fyber|inneractive|digital ?turbine/i },
    { id: "bidmachine",  name: "BidMachine",                re: /bidmachine|appodeal/i },
    { id: "moloco",      name: "Moloco Ads",                re: /moloco/i },
    { id: "ly_ads",      name: "LY Ads (Line)",             re: /\bly ads|\bline\b/i },
];

const isSdkBiddingRow = (r) =>
    r?.requestType === "sdk_bidding" || r?.type === "sdk_bidding";

// Resolve a buyer descriptor from an observed sdk_bidding row.
function buyerForRow(r) {
    const vd = r?.validData || {};
    if (vd.networkId) {
        const byId = BUYERS.find((b) => b.id === vd.networkId);
        if (byId) return byId;
    }
    const nm = vd.networkName || "";
    const byName = BUYERS.find((b) => b.re.test(nm));
    if (byName) return byName;
    // Unknown buyer host that classified as sdk_bidding — keep it visible.
    return { id: vd.networkId || nm || "unknown", name: vd.networkName || "Unknown buyer", re: null };
}

/**
 * @param {Array} rows                 all captured rows (sdk_bidding filtered internally)
 * @param {Array} declaredAdapters     Publisher Settings adapters: [{ name, ... }]
 * @returns {null | {
 *   buyers: Array<{
 *     id, name, declared, observed,
 *     requests, bids, fills, noFills, errors, events,
 *     status: 'ok'|'no_fill'|'error'|'observed'|'silent'|'undeclared',
 *     placements: string[]
 *   }>,
 *   total, declaredCount, observedCount, overall: 'pass'|'warn'|'fail'
 * }}
 */
export function buildMediationHealth(rows = [], declaredAdapters = []) {
    // Declared SDK-Bidding buyers = declared adapters whose name matches a buyer.
    const declaredBuyers = new Map(); // id -> name
    (declaredAdapters || []).forEach((a) => {
        const nm = a?.name || a;
        if (!nm) return;
        const b = BUYERS.find((x) => x.re.test(nm));
        if (b) declaredBuyers.set(b.id, b.name);
    });

    const sbRows = (rows || []).filter(isSdkBiddingRow);

    // Nothing declared and nothing observed → no panel.
    if (declaredBuyers.size === 0 && sbRows.length === 0) return null;

    // Aggregate observed traffic by buyer.
    const obs = new Map(); // id -> aggregate
    sbRows.forEach((r) => {
        const b = buyerForRow(r);
        if (!obs.has(b.id)) {
            obs.set(b.id, {
                id: b.id, name: b.name,
                requests: 0, bids: 0, fills: 0, noFills: 0, errors: 0, events: 0, telemetry: 0,
                placements: new Set(), messages: new Set(),
            });
        }
        const agg = obs.get(b.id);
        const vd = r.validData || {};
        agg.requests += 1;
        if (vd.kind === "bid") {
            agg.bids += 1;
            if (vd.outcome === "filled") agg.fills += 1;
            else if (vd.outcome === "no_fill") agg.noFills += 1;
            else if (vd.outcome === "error") agg.errors += 1;
        } else if (vd.kind === "error_log") {
            // Buyer SDK reporting its own error — highest-signal integration check.
            agg.errors += 1;
        } else if (vd.kind === "telemetry") {
            agg.telemetry += 1;   // SDK active heartbeat
        } else if (vd.kind === "event") {
            agg.events += 1;
        }
        if (vd.outcome === "error") agg.errors = Math.max(agg.errors, 1);
        if (vd.placementId) agg.placements.add(vd.placementId);
        (vd.messages || []).forEach((m) => m && agg.messages.add(m));
    });

    // ---- Secure-signal (a3p) cross-reference ------------------------------
    // Each SDK-Bidding buyer's SDK contributes a bid token to the a3p blob on
    // GAM requests; the proxy decodes it per request (validData.secureSignals,
    // incl. per-buyer sources). Cross-referencing turns a buyer SDK error into
    // a routed diagnosis: signal reaching GAM → the fault is in the adapter's
    // loadAd call; signal absent → upstream (signals never reach the buyer).
    const gamSigRows = (rows || []).filter(
        (r) => (r?.requestType === "google" || r?.type === "google") && r?.validData?.secureSignals
    );
    function signalForBuyer(id) {
        if (!gamSigRows.length) return { status: "unknown", reaching: 0, checked: 0 };
        let reaching = 0, present = 0, seen = 0;
        gamSigRows.forEach((r) => {
            const ss = r.validData.secureSignals;
            if (ss.present) present++;
            const src = (ss.sources || []).find((s) => s.id === id);
            if (src) { seen++; if (src.found) reaching++; }
        });
        // Older proxy builds don't emit buyer sources — unknown, not missing.
        if (!seen) return { status: "unknown", reaching: 0, checked: gamSigRows.length };
        if (reaching > 0) return { status: "reaching", reaching, checked: gamSigRows.length };
        if (present === 0) return { status: "no_a3p", reaching: 0, checked: gamSigRows.length };
        return { status: "missing", reaching: 0, checked: gamSigRows.length };
    }

    // Union of declared + observed buyers.
    const ids = new Set([...declaredBuyers.keys(), ...obs.keys()]);

    const buyers = [...ids].map((id) => {
        const a = obs.get(id);
        const declared = declaredBuyers.has(id);
        const observed = !!a;
        const name = (a && a.name) || declaredBuyers.get(id) || id;

        let status;
        if (a && a.errors > 0) status = "error";
        else if (!observed && declared) status = "silent";       // declared but SDK never fired
        else if (observed && !declared) status = "undeclared";   // firing but not in pub settings
        else if (a && a.fills > 0) status = "ok";
        else if (a && a.bids > 0) status = "no_fill";            // bid(s) but never filled
        else if (observed) status = "observed";                  // only events/config seen
        else status = "silent";

        // a3p cross-reference + fault routing for erroring buyers.
        const signal = signalForBuyer(id);
        let verdictHint = null;
        if (status === "error") {
            if (signal.status === "reaching") verdictHint = "adapter";
            else if (signal.status === "missing" || signal.status === "no_a3p") verdictHint = "upstream";
        }

        return {
            id, name, declared, observed,
            requests: a ? a.requests : 0,
            bids: a ? a.bids : 0,
            fills: a ? a.fills : 0,
            noFills: a ? a.noFills : 0,
            errors: a ? a.errors : 0,
            events: a ? a.events : 0,
            telemetry: a ? a.telemetry : 0,
            placements: a ? [...a.placements] : [],
            messages: a ? [...a.messages].slice(0, 4) : [],
            status,
            signal,
            verdictHint,
        };
    }).sort((x, y) => {
        // surface problems first: error > silent > no_fill > undeclared > observed > ok
        const rank = { error: 0, silent: 1, no_fill: 2, undeclared: 3, observed: 4, ok: 5 };
        return (rank[x.status] - rank[y.status]) || x.name.localeCompare(y.name);
    });

    const overall = buyers.some((b) => b.status === "error")
        ? "fail"
        : buyers.some((b) => b.status === "silent" || b.status === "no_fill" || b.status === "undeclared")
            ? "warn"
            : "pass";

    return {
        buyers,
        total: sbRows.length,
        declaredCount: declaredBuyers.size,
        observedCount: obs.size,
        overall,
    };
}

// Human label + color hint for a buyer status (used by the UI).
export function mediationStatusMeta(status) {
    switch (status) {
        case "ok":         return { label: "filling",      tone: "success" };
        case "no_fill":    return { label: "no fill",      tone: "warn" };
        case "error":      return { label: "error",        tone: "error" };
        case "silent":     return { label: "not observed", tone: "warn" };
        case "undeclared": return { label: "undeclared",   tone: "warn" };
        case "observed":   return { label: "observed",     tone: "neutral" };
        default:           return { label: status,         tone: "neutral" };
    }
}
